import ActionBase, { ACTION_TYPES } from './ActionBase.js';
import Store from '../../Store.js';

/**
 * @module Core/data/stm/action/RemoveAction
 */

/**
 * Action to record the fact of models removed from a store.
 * @extends Core/data/stm/action/ActionBase
 */
export default class RemoveAction extends ActionBase {
    static $name = 'RemoveAction';

    static configurable = {
        /**
         * Reference to a store that models have been removed from.
         *
         * @prp {Core.data.Store}
         * @readonly
         * @default
         */
        store : undefined,

        /**
         * List of models removed from the store.
         *
         * @prp {Core.data.Model[]}
         * @readonly
         * @default
         */
        modelList : undefined,

        /**
         * Models removing context.
         *
         * @prp {Object}
         * @readonly
         * @default
         */
        context : undefined,

        /**
         * Flag showing if undo/redo should be done silently i.e. with events suppressed
         *
         * @prp {Boolean}
         * @readonly
         * @default
         */
        silent : false
    };

    get type() {
        return ACTION_TYPES.REMOVE;
    }

    //<debug>
    afterConfig() {
        super.afterConfig();

        console.assert(
            this.store instanceof Store &&
            Array.isArray(this.modelList) &&
            this.modelList.length &&
            this.modelList.every(m => m.isModel) &&
            "Can't create action, bad configuration!"
        );
    }
    //</debug>

    changeStore(store) {
        //<debug>
        console.assert(
            !this._store && store && store instanceof Store,
            "Can't set store, store is required and can be set only once!"
        );
        //</debug>

        return store;
    }

    changeModelList(list) {
        //<debug>
        console.assert(
            !this._modelList &&
            Array.isArray(list) &&
            list.length &&
            list.every(m => m.isModel),
            "Can't set model list, model list is required, it should be array of Models and it can be set only once!"
        );
        //</debug>

        return list.slice(0);
    }

    changeContext(context) {
        //<debug>
        console.assert(
            !this._context &&
            context instanceof Map &&
            Array.from(context.entries()).every(([k, v]) => k.isModel && typeof v === 'number'),
            "Can't set removal context, removal context is required, it should be Map with Model instances as keys and numeric values, and it can be set only once!"
        );
        //</debug>

        return context;
    }

    undo() {
        const { store, context, modelList, silent } = this;

        // Let's sort models by index such that models with lesser index
        // were inserted back first, thus making valid index of models following.
        modelList.sort((lhs, rhs) => {
            const
                lhsIndex = context.get(lhs),
                rhsIndex = context.get(rhs);

            // Here, in contrast to InsertAction, index is always present
            return lhsIndex - rhsIndex;
        });

        modelList.forEach(m => {
            const index = context.get(m);

            // Insert at previous index
            store.insert(index, m, silent);
        });
    }

    redo() {
        this.store.remove(this.modelList, this.silent);
    }
}
